import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../utils/axiosConfig';

const palette = {
    pageBg: '#f6f8fb',
    border: '#d9e1ec',
    mutedBorder: '#e7edf5',
    panelTint: '#f3f7fd',
    accent: '#1e40af',
    accentDark: '#1d4ed8',
    accentSoft: '#eaf1fb',
    text: '#1b2a3a',
    secondaryText: '#5a6b7f',
    primary: '#1f4e79',
    primarySoft: '#e8f0fa',
    successSoft: '#e8f5ee',
    successBorder: '#8ab89d',
    modalBackdrop: 'rgba(12, 20, 33, 0.46)'
};

const deepClone = (value) => JSON.parse(JSON.stringify(value || {}));

const tableStyles = {
    wrapper: {
        background: '#ffffff',
        borderRadius: '14px',
        border: `1px solid ${palette.border}`,
        boxShadow: '0 2px 12px rgba(43, 70, 99, 0.08)',
        overflow: 'hidden',
        marginBottom: '1.5rem'
    },
    actionsBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0.9rem',
        background: '#f7fbff',
        borderBottom: `1px solid ${palette.mutedBorder}`
    },
    actionsText: {
        margin: 0,
        fontSize: '0.82rem',
        color: palette.secondaryText,
        fontWeight: 600
    },
    actionsButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.55rem'
    },
    saveButton: {
        border: '1px solid #15803d',
        background: '#16a34a',
        color: '#ffffff',
        borderRadius: '8px',
        fontSize: '0.8rem',
        fontWeight: 700,
        padding: '0.36rem 0.8rem',
        cursor: 'pointer'
    },
    cancelButton: {
        border: `1px solid ${palette.border}`,
        background: '#ffffff',
        color: palette.text,
        borderRadius: '8px',
        fontSize: '0.8rem',
        fontWeight: 700,
        padding: '0.36rem 0.8rem',
        cursor: 'pointer'
    },
    tableContainer: {
        width: '100%',
        background: '#ffffff',
        padding: 0
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto',
        background: '#ffffff',
        border: 'none',
        minWidth: '800px'
    },
    axisCell: {
        textAlign: 'left',
        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #1e3a8a 100%)',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.95rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        padding: '1.25rem 1rem',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        borderBottom: 'none'
    },
    axisNumbersLabel: {
        textAlign: 'center',
        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #1e3a8a 100%)',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.95rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        padding: '1.25rem 1rem',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        borderBottom: 'none'
    },
    rowHeader: {
        padding: '1rem',
        color: palette.text,
        fontSize: '0.95rem',
        fontWeight: 600,
        borderRight: `1px solid ${palette.border}`,
        borderBottom: `1px solid ${palette.mutedBorder}`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: '#ffffff'
    },
    dataCell: {
        borderBottom: `1px solid ${palette.mutedBorder}`,
        borderRight: `1px solid ${palette.mutedBorder}`,
        padding: '1rem'
    },
    footer: {
        padding: '0.78rem 1.05rem 0.92rem',
        borderTop: `1px solid ${palette.mutedBorder}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.9rem',
        color: palette.secondaryText,
        background: '#fcfdff'
    },
    footerMetric: {
        fontSize: '0.84rem',
        fontWeight: 600
    },
    statusLine: {
        padding: '0.55rem 0.9rem 0',
        fontSize: '0.79rem',
        fontWeight: 600
    }
};

const modalStyles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: `linear-gradient(180deg, ${palette.modalBackdrop} 0%, rgba(12, 20, 33, 0.56) 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem'
    },
    card: {
        width: 'min(980px, 94vw)',
        maxHeight: '86vh',
        overflow: 'hidden',
        borderRadius: '14px',
        border: `1px solid ${palette.border}`,
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
        boxShadow: '0 28px 66px rgba(4, 18, 36, 0.28)'
    },
    head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.9rem 1rem',
        borderBottom: `1px solid ${palette.mutedBorder}`,
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`
    },
    headTitle: {
        margin: 0,
        fontSize: '1.02rem',
        color: '#ffffff',
        fontWeight: 700
    },
    headSub: {
        margin: '0.22rem 0 0',
        fontSize: '0.84rem',
        color: '#d8e8fb',
        fontWeight: 500
    },
    closeButton: {
        border: '1px solid rgba(255, 255, 255, 0.38)',
        background: 'rgba(255, 255, 255, 0.14)',
        color: '#ffffff',
        borderRadius: '999px',
        width: '32px',
        height: '32px',
        cursor: 'pointer',
        fontSize: '1.28rem',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500
    },
    summary: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.65rem',
        padding: '0.8rem 1.1rem',
        borderBottom: `1px solid ${palette.mutedBorder}`,
        background: `linear-gradient(180deg, ${palette.accentSoft} 0%, #f8fbff 100%)`
    },
    summaryCard: {
        border: '1px solid #d6e3f4',
        borderRadius: '8px',
        padding: '0.55rem 0.65rem',
        background: '#ffffff'
    },
    summaryLabel: {
        fontSize: '0.74rem',
        color: palette.secondaryText,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        fontWeight: 700,
        marginBottom: '0.2rem'
    },
    summaryValue: {
        fontSize: '0.92rem',
        color: palette.text,
        fontWeight: 700
    },
    body: {
        maxHeight: '58vh',
        overflowY: 'auto',
        padding: '0.9rem 1.1rem 1.1rem'
    },
    preceptorCard: {
        border: `1px solid ${palette.mutedBorder}`,
        borderRadius: '10px',
        marginBottom: '0.75rem',
        overflow: 'hidden'
    },
    preceptorHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.8rem',
        background: 'linear-gradient(180deg, #f8fbff 0%, #f4f8fe 100%)',
        padding: '0.7rem 0.85rem',
        borderBottom: `1px solid ${palette.mutedBorder}`
    },
    preceptorName: {
        margin: 0,
        fontSize: '0.9rem',
        color: palette.text,
        fontWeight: 700
    },
    availabilityBadge: {
        background: `linear-gradient(180deg, ${palette.primarySoft} 0%, #dce8f7 100%)`,
        border: `1px solid ${palette.border}`,
        borderRadius: '6px',
        padding: '0.2rem 0.45rem',
        color: palette.primary,
        fontSize: '0.78rem',
        fontWeight: 700
    },
    availabilityEditor: {
        width: '74px',
        border: `1px solid ${palette.border}`,
        borderRadius: '6px',
        padding: '0.24rem 0.35rem',
        fontSize: '0.76rem',
        fontWeight: 600,
        color: palette.text,
        background: '#ffffff'
    },
    availabilityEditBtn: {
        border: '1px solid #1d4ed8',
        background: '#2563eb',
        color: '#ffffff',
        borderRadius: '7px',
        padding: '0.28rem 0.55rem',
        fontSize: '0.76rem',
        fontWeight: 700,
        cursor: 'pointer'
    },
    availabilityEditInline: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.35rem'
    },
    availabilitySaveBtn: {
        border: '1px solid #15803d',
        background: '#16a34a',
        color: '#ffffff',
        borderRadius: '7px',
        padding: '0.26rem 0.5rem',
        fontSize: '0.74rem',
        fontWeight: 700,
        cursor: 'pointer'
    },
    availabilityCancelBtn: {
        border: `1px solid ${palette.border}`,
        background: '#ffffff',
        color: palette.text,
        borderRadius: '7px',
        padding: '0.26rem 0.5rem',
        fontSize: '0.74rem',
        fontWeight: 700,
        cursor: 'pointer'
    },
    studentsBox: {
        padding: '0.75rem 0.85rem'
    },
    studentsLabel: {
        margin: 0,
        fontSize: '0.76rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: palette.secondaryText
    },
    studentsList: {
        margin: '0.55rem 0 0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.45rem'
    },
    studentChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        borderRadius: '999px',
        border: `1px solid ${palette.border}`,
        background: '#ffffff',
        color: palette.text,
        padding: '0.26rem 0.55rem',
        fontSize: '0.77rem',
        fontWeight: 600
    },
    studentRemoveBtn: {
        border: 'none',
        background: 'transparent',
        color: '#b91c1c',
        cursor: 'pointer',
        fontSize: '0.95rem',
        lineHeight: 1,
        fontWeight: 700,
        padding: 0
    },
    addStudentBtn: {
        marginTop: '0.55rem',
        border: '1px solid #15803d',
        background: '#16a34a',
        color: '#ffffff',
        borderRadius: '8px',
        padding: '0.34rem 0.62rem',
        cursor: 'pointer',
        fontSize: '0.74rem',
        fontWeight: 700
    },
    emptyMessage: {
        marginTop: '0.55rem',
        color: palette.secondaryText,
        fontSize: '0.8rem',
        fontStyle: 'italic'
    }
};

const getFirstNonEmpty = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return null;
};

const normalizeStudentToken = (value) => {
    if (value === undefined || value === null) return '';
    let token = String(value).trim().toLowerCase();
    if (token.startsWith('co-')) {
        token = token.slice(3);
    }
    return token;
};

const isLikelyIdentifier = (value) => {
    const token = String(value || '').trim();
    if (!token) return false;
    if (/^[a-f0-9]{24}$/i.test(token)) return true;
    if (/^\d+$/.test(token)) return true;
    if (/^[a-z0-9_-]{6,}$/i.test(token) && !token.includes(' ')) return true;
    return false;
};

const getStudentRefs = (studentValue) => {
    if (studentValue === undefined || studentValue === null) {
        return [];
    }

    if (typeof studentValue === 'object') {
        const idLikeRefs = [
            studentValue.studentId,
            studentValue.student_id,
            studentValue._id,
            studentValue.id
        ]
            .map(normalizeStudentToken)
            .filter(Boolean);

        if (idLikeRefs.length > 0) {
            return Array.from(new Set(idLikeRefs));
        }

        const nameFallback = normalizeStudentToken(studentValue.name || studentValue.fullName);
        return nameFallback ? [nameFallback] : [];
    }

    const token = normalizeStudentToken(studentValue);
    return token ? [token] : [];
};

const getStudentNameFromValue = (studentValue) => {
    if (studentValue === undefined || studentValue === null) {
        return '';
    }

    if (typeof studentValue === 'object') {
        const fromNames = getFirstNonEmpty(
            studentValue.name,
            studentValue.fullName,
            `${studentValue.firstname || ''} ${studentValue.lastname || ''}`.trim()
        );
        if (fromNames) {
            return String(fromNames).trim();
        }
        const fallbackId = getFirstNonEmpty(
            studentValue.studentId,
            studentValue.student_id,
            studentValue._id,
            studentValue.id
        );
        return fallbackId ? String(fallbackId).trim() : '';
    }

    return String(studentValue).trim();
};

const AvailabilityMatrixTable = ({
    scheduleData,
    type = 'experience',
    curriculumId = '',
    curriculumName = '',
    selectedStudent = null,
    selectedStudentId = null,
    selectedStudentName = '',
    onDirtyChange = null
}) => {
    const [selectedCell, setSelectedCell] = useState(null);
    const [workingScheduleData, setWorkingScheduleData] = useState(() => deepClone(scheduleData));
    const [hasPendingEdits, setHasPendingEdits] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [availabilityEdits, setAvailabilityEdits] = useState({});
    const [editedStudentRotations, setEditedStudentRotations] = useState({});
    const [curriculumRotationCount, setCurriculumRotationCount] = useState(8);
    const rotations = workingScheduleData?.rotations || [];

    useEffect(() => {
        setWorkingScheduleData(deepClone(scheduleData));
        setHasPendingEdits(false);
        setAvailabilityEdits({});
        setEditedStudentRotations({});
        setSelectedCell(null);
        setSaveError('');
        setSaveSuccess('');
    }, [scheduleData, selectedStudent]);

    useEffect(() => {
        let cancelled = false;

        const fallbackCount = () => {
            const fromRotations = Array.isArray(rotations) ? rotations.length : 0;
            return Math.max(1, fromRotations || 8);
        };

        const resolveCurriculumRotationCount = async () => {
            if (!curriculumId) {
                setCurriculumRotationCount(fallbackCount());
                return;
            }

            try {
                const response = await axiosInstance.get(`/config/get_configuration_by_curriculum/${curriculumId}`);
                const cfg = Array.isArray(response.data) ? response.data[0] : response.data;
                const fromRotations = Array.isArray(cfg?.rotations) ? cfg.rotations.length : 0;
                const fromCount = parseInt(cfg?.rotationsCount, 10);
                const fromNumber = parseInt(cfg?.rotationsNumber, 10);
                const resolved = Math.max(1, fromRotations || fromCount || fromNumber || fallbackCount());
                if (!cancelled) {
                    setCurriculumRotationCount(resolved);
                }
            } catch {
                if (!cancelled) {
                    setCurriculumRotationCount(fallbackCount());
                }
            }
        };

        resolveCurriculumRotationCount();

        return () => {
            cancelled = true;
        };
    }, [curriculumId, rotations]);

    useEffect(() => {
        if (typeof onDirtyChange === 'function') {
            onDirtyChange(curriculumId, Boolean(selectedStudent) && hasPendingEdits);
        }
    }, [curriculumId, hasPendingEdits, selectedStudent, onDirtyChange]);

    const markRotationEdited = (rotationNumber) => {
        recordMatrixScheduleChange(rotationNumber);
        setEditedStudentRotations((prev) => ({
            ...prev,
            [String(rotationNumber)]: true
        }));
    };

    const selectedStudentTokens = useMemo(() => {
        const tokens = [selectedStudentId, selectedStudent, selectedStudentName]
            .map(normalizeStudentToken)
            .filter(Boolean);
        return Array.from(new Set(tokens));
    }, [selectedStudentId, selectedStudent, selectedStudentName]);

    const selectedStudentLabel = selectedStudentName || selectedStudent || selectedStudentId || 'Selected student';

    const getStudentDisplayMap = (preceptorData) => {
        const displayMap = new Map();
        const students = Array.isArray(preceptorData?.students) ? preceptorData.students : [];
        const studentsData = Array.isArray(preceptorData?.students_data) ? preceptorData.students_data : [];

        studentsData.forEach((studentEntry) => {
            const refs = getStudentRefs(studentEntry);
            const displayName = getStudentNameFromValue(studentEntry);
            const isCoprecepted = Boolean(studentEntry?.isCoprecepted) || refs.some((ref) => ref.startsWith('co-'));
            refs.forEach((ref) => {
                const preferredName = selectedStudentTokens.includes(ref) && selectedStudentName
                    ? selectedStudentName
                    : displayName;
                if (!displayMap.has(ref) && preferredName) {
                    displayMap.set(ref, {
                        displayName: preferredName,
                        isCoprecepted
                    });
                }
            });
        });

        students.forEach((studentEntry) => {
            const refs = getStudentRefs(studentEntry);
            const displayName = getStudentNameFromValue(studentEntry);
            const isCoprecepted = refs.some((ref) => ref.startsWith('co-'));
            refs.forEach((ref) => {
                const preferredName = selectedStudentTokens.includes(ref) && selectedStudentName
                    ? selectedStudentName
                    : displayName;
                if (!displayMap.has(ref)) {
                    displayMap.set(ref, {
                        displayName: preferredName || ref,
                        isCoprecepted
                    });
                }
            });
        });

        return displayMap;
    };

    const getMergedStudentRows = (preceptorData) =>
        Array.from(getStudentDisplayMap(preceptorData).entries()).map(([token, studentInfo]) => ({
            token,
            displayName: studentInfo?.displayName || token,
            isCoprecepted: Boolean(studentInfo?.isCoprecepted)
        }));

    const preceptorHasSelectedStudent = (preceptorData) => {
        const students = Array.isArray(preceptorData?.students) ? preceptorData.students : [];
        const studentsData = Array.isArray(preceptorData?.students_data) ? preceptorData.students_data : [];
        return students.some(matchesSelectedStudent) || studentsData.some(matchesSelectedStudent);
    };

    const removeSelectedStudentFromPreceptor = (preceptorData) => {
        if (!preceptorData) return;
        const students = Array.isArray(preceptorData.students) ? preceptorData.students : [];
        const studentsData = Array.isArray(preceptorData.students_data) ? preceptorData.students_data : [];

        preceptorData.students = students.filter((studentRef) => !matchesSelectedStudent(studentRef));
        preceptorData.students_data = studentsData.filter((studentRef) => !matchesSelectedStudent(studentRef));
    };

    const getSelectedStudentRefToken = () => {
        const candidate = getFirstNonEmpty(selectedStudentId, selectedStudent, selectedStudentName);
        return candidate ? String(candidate).trim() : null;
    };

    const matchesSelectedStudent = (studentValue) => {
        if (!selectedStudentTokens.length) {
            return false;
        }
        const refs = getStudentRefs(studentValue);
        if (!refs.length) {
            return false;
        }
        return refs.some((ref) => selectedStudentTokens.includes(ref));
    };

    const allExperiencesOrInstitutions = useMemo(() => {
        const matrix = new Map();
        rotations.forEach((rotation) => {
            const key = type === 'experience' ? 'specialities' : 'institutions';
            const itemsData = rotation[key] || {};

            Object.entries(itemsData).forEach(([name, data]) => {
                if (!matrix.has(name)) {
                    matrix.set(name, new Map());
                }
                matrix.get(name).set(rotation.rotation, data);
            });
        });
        return matrix;
    }, [rotations, type]);

    const sortedRotations = useMemo(() => {
        const ordered = [...new Set(rotations.map((r) => r.rotation))].sort((a, b) => a - b);
        const required = Math.max(1, curriculumRotationCount || 8);
        return ordered.slice(0, required);
    }, [rotations, curriculumRotationCount]);

    const rotationIndexLookup = useMemo(() => {
        const map = new Map();
        sortedRotations.forEach((rotationValue, index) => {
            if (rotationValue !== undefined && rotationValue !== null) {
                if (!map.has(rotationValue)) {
                    map.set(rotationValue, index);
                }
                const stringValue = String(rotationValue);
                if (!map.has(stringValue)) {
                    map.set(stringValue, index);
                }
            }
        });
        return map;
    }, [sortedRotations]);

    const getRotationIndex = useCallback(
        (rotationNumber) => {
            if (rotationNumber === undefined || rotationNumber === null) {
                return -1;
            }

            if (rotationIndexLookup.has(rotationNumber)) {
                return rotationIndexLookup.get(rotationNumber);
            }

            const numericRotation = Number(rotationNumber);
            if (!Number.isNaN(numericRotation) && Number.isInteger(numericRotation)) {
                if (rotationIndexLookup.has(numericRotation)) {
                    return rotationIndexLookup.get(numericRotation);
                }
                if (numericRotation >= 0 && numericRotation < sortedRotations.length) {
                    return numericRotation;
                }
                if (numericRotation >= 1 && numericRotation <= sortedRotations.length) {
                    return numericRotation - 1;
                }
            }

            const stringKey = String(rotationNumber);
            if (rotationIndexLookup.has(stringKey)) {
                return rotationIndexLookup.get(stringKey);
            }

            return -1;
        },
        [rotationIndexLookup, sortedRotations.length]
    );

    const getMatrixEditingStateKey = useCallback(() => {
        if (!curriculumId) return null;
        const studentRef = selectedStudentId || selectedStudent;
        if (!studentRef) return null;
        if (selectedStudentName && curriculumName) {
            return `${selectedStudentName}-${curriculumName}`;
        }
        return `${String(studentRef)}-${curriculumId}`;
    }, [selectedStudent, selectedStudentId, selectedStudentName, curriculumId, curriculumName]);

    const cleanupMatrixEditingState = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const key = getMatrixEditingStateKey();
        if (!key || !window.editingState) {
            return;
        }
        if (window.editingState[key]) {
            delete window.editingState[key];
            if (!Object.keys(window.editingState).length) {
                delete window.editingState;
            }
        }
    }, [getMatrixEditingStateKey]);

    useEffect(() => {
        return () => cleanupMatrixEditingState();
    }, [cleanupMatrixEditingState]);

    const recordMatrixScheduleChange = useCallback(
        (rotationNumber) => {
            if (rotationNumber === null || rotationNumber === undefined) {
                return;
            }
            if (!curriculumId || typeof window === 'undefined') {
                return;
            }
            const key = getMatrixEditingStateKey();
            if (!key) {
                return;
            }
            const editingState = window.editingState || {};
            const entryKey = String(selectedStudentId || selectedStudent);
            const changeEntry = editingState[key] || {
                studentId: entryKey,
                curriculumId,
                originalSchedule: [],
                changes: []
            };
            const isAlreadyTracked = changeEntry.changes.some(
                (change) => change.rotationNumber === rotationNumber
            );
            if (!isAlreadyTracked) {
                changeEntry.changes.push({
                    rotationNumber,
                    rotationIndex: getRotationIndex(rotationNumber),
                    source: 'availability-matrix',
                    timestamp: new Date().toISOString()
                });
            }
            editingState[key] = changeEntry;
            window.editingState = editingState;
        },
        [curriculumId, getMatrixEditingStateKey, getRotationIndex, selectedStudent, selectedStudentId]
    );

    const sortedExperiencesOrInstitutions = useMemo(
        () => Array.from(allExperiencesOrInstitutions.keys()).sort(),
        [allExperiencesOrInstitutions]
    );

    const axisLabel = type === 'experience' ? 'Experience' : 'Institution';

    const getCellData = (name, rotation) => allExperiencesOrInstitutions.get(name)?.get(rotation) || null;

    const isStudentAssignedInRotation = (rotationNumber) => {
        if (!selectedStudent) return false;

        for (const itemName of sortedExperiencesOrInstitutions) {
            const cellData = getCellData(itemName, rotationNumber);
            if (!cellData || !cellData.preceptors) {
                continue;
            }
            for (const preceptorData of Object.values(cellData.preceptors)) {
                if (preceptorHasSelectedStudent(preceptorData)) {
                    return true;
                }
            }
        }

        return false;
    };

    const findPreceptorIdByName = (targetName) => {
        const key = type === 'experience' ? 'specialities' : 'institutions';
        for (const rotation of rotations) {
            const items = rotation?.[key] || {};
            for (const itemData of Object.values(items)) {
                const preceptors = itemData?.preceptors || {};
                for (const [candidateName, candidateData] of Object.entries(preceptors)) {
                    if (String(candidateName || '').trim().toLowerCase() === String(targetName || '').trim().toLowerCase()) {
                        const candidateId = getFirstNonEmpty(
                            candidateData?.preceptor_id,
                            candidateData?.preceptorId,
                            candidateData?._id,
                            candidateData?.id,
                            null
                        );
                        if (candidateId) {
                            return candidateId;
                        }
                    }
                }
            }
        }
        return null;
    };

    const getPreceptorId = (preceptorName, preceptorData) => {
        const direct = getFirstNonEmpty(
            preceptorData?.preceptor_id,
            preceptorData?.preceptorId,
            preceptorData?._id,
            preceptorData?.id,
            null
        );
        if (direct) {
            return direct;
        }

        const keyToken = String(preceptorName || '').trim();
        if (isLikelyIdentifier(keyToken)) {
            return keyToken;
        }

        const fromLookup = findPreceptorIdByName(preceptorName);
        return fromLookup || null;
    };

    const getAvailabilityEditKey = (preceptorName, preceptorData) => {
        const resolvedId = getPreceptorId(preceptorName, preceptorData);
        if (resolvedId) {
            return `id:${resolvedId}`;
        }
        const fallbackName = String(preceptorName || '').trim().toLowerCase();
        return fallbackName ? `name:${fallbackName}` : null;
    };

    const matchesPreceptor = (targetId, targetName, candidateName, candidateData) => {
        const candidateId = getPreceptorId(candidateName, candidateData);
        if (targetId && candidateId) {
            return String(targetId) === String(candidateId);
        }
        return String(targetName || '').trim().toLowerCase() === String(candidateName || '').trim().toLowerCase();
    };

    const getAvailabilityBaseForPreceptor = (targetId, targetName) => {
        const base = Array(Math.max(1, curriculumRotationCount || 8)).fill(0);
        const key = type === 'experience' ? 'specialities' : 'institutions';

        rotations.forEach((rotation) => {
            const itemsData = rotation[key] || {};
            Object.entries(itemsData).forEach(([, itemData]) => {
                Object.entries(itemData?.preceptors || {}).forEach(([candidateName, candidateData]) => {
                    if (matchesPreceptor(targetId, targetName, candidateName, candidateData)) {
                        const availability = Math.max(0, parseInt(candidateData?.availability, 10) || 0);
                        if (rotation.rotation >= 0 && rotation.rotation < base.length) {
                            base[rotation.rotation] = availability;
                        }
                    }
                });
            });
        });

        return base;
    };

    const updateRotationData = (rotationNumber, mutator, markStudentDirty = true) => {
        setWorkingScheduleData((prev) => {
            const next = deepClone(prev);
            if (!next || !Array.isArray(next.rotations)) {
                return prev;
            }
            const rotationEntry = next.rotations.find((rotation) => rotation.rotation === rotationNumber);
            if (!rotationEntry) {
                return prev;
            }

            const key = type === 'experience' ? 'specialities' : 'institutions';
            rotationEntry[key] = rotationEntry[key] || {};
            mutator(rotationEntry[key]);
            return next;
        });
        if (markStudentDirty) {
            setHasPendingEdits(true);
        }
        setSaveError('');
        setSaveSuccess('');
    };

    const handleAvailabilityDraftChange = (preceptorName, preceptorData, rotationNumber, nextValue) => {
        const parsed = Math.max(0, Math.min(99, parseInt(nextValue, 10) || 0));
        const preceptorId = getPreceptorId(preceptorName, preceptorData);
        const editKey = getAvailabilityEditKey(preceptorName, preceptorData);

        if (!editKey || !curriculumId) {
            setSaveError('This preceptor cannot be edited in the current state.');
            return;
        }

        setAvailabilityEdits((prev) => {
            const current = prev[editKey];
            const base = current?.base || getAvailabilityBaseForPreceptor(preceptorId, preceptorName);
            const draft = Array.from({ length: base.length }, (_, index) => {
                const raw = current?.draft?.[index] ?? base[index] ?? 0;
                const parsed = parseInt(raw, 10);
                return Math.max(0, Math.min(99, Number.isNaN(parsed) ? 0 : parsed));
            });
            if (rotationNumber >= 0 && rotationNumber < draft.length) {
                draft[rotationNumber] = parsed;
            }
            return {
                ...prev,
                [editKey]: {
                    editKey,
                    preceptorId,
                    preceptorName,
                    base,
                    draft,
                    isSaving: false
                }
            };
        });
        setSaveError('');
        setSaveSuccess('');
    };

    const saveAvailabilityForPreceptor = async (preceptorName, preceptorData, rotationNumber) => {
        const preceptorId = getPreceptorId(preceptorName, preceptorData);
        const editKey = getAvailabilityEditKey(preceptorName, preceptorData);
        // This matrix renders either an experience axis or an institution axis
        // (see the `type` prop) — the matching entity id follows whichever this is.
        const entityId = type === 'experience'
            ? getFirstNonEmpty(
                preceptorData?.specialityId,
                preceptorData?.speciality_id,
                selectedCellData?.specialityId,
                selectedCellData?.speciality_id
            )
            : getFirstNonEmpty(
                preceptorData?.institutionId,
                preceptorData?.institution_id,
                selectedCellData?.institutionId,
                selectedCellData?.institution_id
            );
        if (!preceptorId || !curriculumId) {
            setSaveError('This preceptor does not have a resolvable ID for availability updates.');
            return;
        }
        if (!entityId) {
            setSaveError(`This preceptor does not have a resolvable ${type === 'experience' ? 'speciality' : 'institution'} for availability updates.`);
            return;
        }

        const edit = editKey ? availabilityEdits[editKey] : null;
        const base = edit?.base || getAvailabilityBaseForPreceptor(preceptorId, preceptorName);
        const draft = Array.from({ length: base.length }, (_, index) => {
            const raw = edit?.draft?.[index] ?? base[index] ?? 0;
            const parsed = parseInt(raw, 10);
            return Math.max(0, Math.min(99, Number.isNaN(parsed) ? 0 : parsed));
        });

        setAvailabilityEdits((prev) => ({
            ...prev,
            [editKey]: {
                ...(prev[editKey] || {}),
                editKey,
                preceptorId,
                preceptorName,
                base,
                draft,
                isSaving: true
            }
        }));

        try {
            const availabilityPayload = {
                preceptor_id: preceptorId,
                curriculum_id: curriculumId,
                entity_id: entityId,
                new_availability: JSON.stringify(draft)
            };

            const availabilityAttempts = [
                () => axiosInstance.put('/crud/admin/preceptor/availability', null, { params: availabilityPayload }),
                () => axiosInstance.put('/crud/admin/preceptor/availability', availabilityPayload, {
                    headers: { 'Content-Type': 'application/json' }
                })
            ];

            let availabilitySaved = false;
            let lastAvailabilityError = null;
            for (const attempt of availabilityAttempts) {
                try {
                    await attempt();
                    availabilitySaved = true;
                    break;
                } catch (error) {
                    lastAvailabilityError = error;
                }
            }

            if (!availabilitySaved) {
                throw lastAvailabilityError || new Error('Failed to update preceptor availability.');
            }

            updateRotationData(
                rotationNumber,
                (items) => {
                    Object.values(items || {}).forEach((itemData) => {
                        const preceptors = itemData.preceptors || {};
                        Object.entries(preceptors).forEach(([candidateName, candidateData]) => {
                            if (matchesPreceptor(preceptorId, preceptorName, candidateName, candidateData)) {
                                candidateData.availability = draft[rotationNumber] || 0;
                            }
                        });

                        const currentTotal = Object.values(preceptors).reduce(
                            (sum, preceptor) => sum + (parseInt(preceptor?.availability, 10) || 0),
                            0
                        );
                        itemData.currentTotalAvailability = currentTotal;
                    });
                },
                false
            );

            setAvailabilityEdits((prev) => {
                const next = { ...prev };
                if (editKey) {
                    delete next[editKey];
                }
                return next;
            });
            setSaveSuccess(`Availability updated for ${preceptorName}.`);
        } catch (error) {
            setAvailabilityEdits((prev) => ({
                ...prev,
                [editKey]: {
                    ...(prev[editKey] || {}),
                    isSaving: false
                }
            }));
            setSaveError(
                error.response?.data?.detail ||
                    error.response?.data?.message ||
                    `Failed to update availability for ${preceptorName}.`
            );
        }
    };

    const cancelAvailabilityEdit = (preceptorName, preceptorData) => {
        const editKey = getAvailabilityEditKey(preceptorName, preceptorData);
        if (!editKey) {
            return;
        }
        setAvailabilityEdits((prev) => {
            const next = { ...prev };
            delete next[editKey];
            return next;
        });
    };

    const removeStudentFromRotation = (rotationNumber) => {
        if (!selectedStudent) return;

        markRotationEdited(rotationNumber);

        updateRotationData(rotationNumber, (items) => {
            Object.values(items || {}).forEach((itemData) => {
                const preceptors = itemData.preceptors || {};
                Object.values(preceptors).forEach((preceptorData) => {
                    removeSelectedStudentFromPreceptor(preceptorData);
                });
            });
        });
    };

    const addStudentToPreceptor = (rotationNumber, itemName, preceptorName) => {
        if (!selectedStudent) return;

        markRotationEdited(rotationNumber);

        const selectedStudentRef = getSelectedStudentRefToken();
        if (!selectedStudentRef) {
            setSaveError('Selected student reference is missing. Please reselect the student and try again.');
            return;
        }

        updateRotationData(rotationNumber, (items) => {
            Object.values(items || {}).forEach((itemData) => {
                const preceptors = itemData.preceptors || {};
                Object.values(preceptors).forEach((preceptorData) => {
                    removeSelectedStudentFromPreceptor(preceptorData);
                });
            });

            const targetCell = items[itemName];
            const targetPreceptor = targetCell?.preceptors?.[preceptorName];
            if (!targetPreceptor) {
                return;
            }
            if (!Array.isArray(targetPreceptor.students)) {
                targetPreceptor.students = [];
            }
            if (!Array.isArray(targetPreceptor.students_data)) {
                targetPreceptor.students_data = [];
            }
            if (!targetPreceptor.students.some(matchesSelectedStudent)) {
                targetPreceptor.students.push(selectedStudentRef);
            }
            if (!targetPreceptor.students_data.some(matchesSelectedStudent)) {
                targetPreceptor.students_data.push({
                    studentId: selectedStudentId || selectedStudentRef,
                    name: selectedStudentName || selectedStudentLabel
                });
            }
        });
    };

    const cellContainsStudent = (cellData) => {
        if (!selectedStudent || !cellData || !cellData.preceptors) {
            return false;
        }
        return Object.values(cellData.preceptors).some((preceptor) => preceptorHasSelectedStudent(preceptor));
    };

    const openCellModal = (name, rotation) => {
        const data = getCellData(name, rotation);
        if (!data || Object.keys(data.preceptors || {}).length === 0) {
            return;
        }
        setSelectedCell({ name, rotation });
    };

    const closeCellModal = () => {
        setSelectedCell(null);
    };

    useEffect(() => {
        const onEscape = (event) => {
            if (event.key === 'Escape') {
                closeCellModal();
            }
        };
        window.addEventListener('keydown', onEscape);
        return () => window.removeEventListener('keydown', onEscape);
    }, []);

    const saveAllChanges = async () => {
        if (!hasPendingEdits) {
            return;
        }

        setIsSaving(true);
        setSaveError('');
        setSaveSuccess('');

        try {
            if (!selectedStudent || !selectedStudentId || !curriculumId) {
                throw new Error('Missing student or curriculum ID for saving student schedule.');
            }

            const rotationDefaults = {
                preceptor_id: null,
                speciality_id: null,
                institution_id: '',
                copreceptor_id: null
            };

            const editedRotationNumbers = Object.keys(editedStudentRotations)
                .map((value) => parseInt(value, 10))
                .filter((value) => !Number.isNaN(value));

            if (!editedRotationNumbers.length) {
                throw new Error('No edited rotations detected. Please edit a rotation before saving.');
            }

            const scheduleLength = Math.max(
                1,
                sortedRotations.length,
                ...sortedRotations.map((rotationNumber) => {
                    const idx = getRotationIndex(rotationNumber);
                    return idx >= 0 ? idx + 1 : 0;
                })
            );

            const schedulePayload = Array.from({ length: scheduleLength }, () => ({ ...rotationDefaults }));

            sortedRotations.forEach((rotationNumber) => {
                let foundItemData = null;
                let foundPreceptorData = null;
                let foundPreceptorName = '';

                    for (const itemName of sortedExperiencesOrInstitutions) {
                        const cellData = getCellData(itemName, rotationNumber);
                        if (!cellData || !cellData.preceptors) {
                            continue;
                        }

                        for (const [candidatePreceptorName, preceptorData] of Object.entries(cellData.preceptors)) {
                            if (preceptorHasSelectedStudent(preceptorData)) {
                                foundItemData = cellData;
                                foundPreceptorData = preceptorData;
                                foundPreceptorName = candidatePreceptorName;
                                break;
                            }
                        }

                        if (foundPreceptorData) {
                            break;
                        }
                    }

                    const rotationIndex = getRotationIndex(rotationNumber);
                    if (rotationIndex < 0 || rotationIndex >= schedulePayload.length || !foundPreceptorData) {
                        return;
                    }

                    schedulePayload[rotationIndex] = {
                        preceptor_id: (() => {
                            const id = getPreceptorId(foundPreceptorName, foundPreceptorData);
                            return id == null || id === '' ? null : String(id);
                        })(),
                        speciality_id: getFirstNonEmpty(
                            foundPreceptorData.speciality_id,
                            foundPreceptorData.specialityId,
                            foundItemData?.speciality_id,
                            foundItemData?.specialityId
                        ),
                        institution_id: getFirstNonEmpty(
                            foundPreceptorData.institution_id,
                            foundPreceptorData.institutionId,
                            foundItemData?.institution_id,
                            foundItemData?.institutionId,
                            ''
                        ),
                        copreceptor_id: getFirstNonEmpty(
                            foundPreceptorData.copreceptor_id,
                            foundPreceptorData.copreceptorId,
                            null
                        )
                    };
                });

            const rawStudentId = String(selectedStudentId);
            const rawCurriculumId = String(curriculumId);

            // Fill missing speciality/institution IDs from curriculum preceptor roster when matrix cell payload lacks them.
            const preceptorMetaById = new Map();
            try {
                const preceptorsRes = await axiosInstance.get(`/curriculum/${encodeURIComponent(rawCurriculumId)}/preceptors`);
                const preceptorsSource = Array.isArray(preceptorsRes.data)
                    ? preceptorsRes.data
                    : Array.isArray(preceptorsRes.data?.preceptors)
                        ? preceptorsRes.data.preceptors
                        : [];

                preceptorsSource.forEach((p) => {
                    const pid = p?.preceptorId ?? p?._id ?? p?.id;
                    if (pid == null || pid === '') return;
                    preceptorMetaById.set(String(pid), {
                        specialityId: p?.specialityId != null && p?.specialityId !== '' ? String(p.specialityId) : null,
                        institutionId: p?.institutionId != null ? String(p.institutionId) : ''
                    });
                });
            } catch (_rosterError) {
                // Keep save flow resilient; rely on matrix payload if roster lookup fails.
            }

            const normalizedSchedulePayload = schedulePayload.map((entry) => ({
                preceptor_id: entry.preceptor_id == null || entry.preceptor_id === '' ? null : String(entry.preceptor_id),
                speciality_id: entry.speciality_id == null || entry.speciality_id === '' ? null : String(entry.speciality_id),
                institution_id: entry.institution_id == null ? '' : String(entry.institution_id),
                copreceptor_id: entry.copreceptor_id == null || entry.copreceptor_id === '' ? null : String(entry.copreceptor_id)
            }));

            normalizedSchedulePayload.forEach((entry) => {
                if (!entry.preceptor_id) return;
                const meta = preceptorMetaById.get(String(entry.preceptor_id));
                if (!meta) return;
                if (!entry.speciality_id && meta.specialityId) {
                    entry.speciality_id = meta.specialityId;
                }
                if (!entry.institution_id && meta.institutionId != null) {
                    entry.institution_id = String(meta.institutionId);
                }
            });

            const saveEndpoints = [
                `/crud/update_student_schedule/${rawStudentId}/${rawCurriculumId}`,
                `/crud/update_student_schedule/${rawStudentId}/${encodeURIComponent(rawCurriculumId)}`
            ];

            let lastSaveError = null;
            let saved = false;
            for (const endpoint of saveEndpoints) {
                try {
                    await axiosInstance.put(
                        endpoint,
                        { schedule: normalizedSchedulePayload },
                        { headers: { 'Content-Type': 'application/json' } }
                    );
                    saved = true;
                    break;
                } catch (error) {
                    lastSaveError = error;
                }
            }

            if (!saved) {
                throw lastSaveError || new Error('Failed to save schedule changes.');
            }

            setHasPendingEdits(false);
            setSelectedCell(null);
            setEditedStudentRotations({});
            setSaveSuccess('Changes saved successfully.');
            cleanupMatrixEditingState();
        } catch (error) {
            const responseData = error?.response?.data;
            const responseText =
                typeof responseData === 'string'
                    ? responseData
                    : responseData?.detail || responseData?.message || responseData?.error;
            setSaveError(
                responseText ||
                error?.message ||
                'Failed to save changes.'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const cancelSelectedStudentScheduleChanges = () => {
        setWorkingScheduleData(deepClone(scheduleData));
        setHasPendingEdits(false);
        setAvailabilityEdits({});
        setEditedStudentRotations({});
        setSaveError('');
        setSaveSuccess('');
        setSelectedCell(null);
        cleanupMatrixEditingState();
    };

    if (!scheduleData || rotations.length === 0) {
        return (
            <div
                style={{
                    padding: '1.3rem',
                    border: `1px solid ${palette.border}`,
                    background: '#ffffff',
                    borderRadius: '12px',
                    color: palette.secondaryText,
                    fontWeight: 500
                }}
            >
                No schedule data available for this curriculum.
            </div>
        );
    }

    let totalCurrent = 0;
    let totalOriginal = 0;
    let totalPreceptors = 0;
    let totalStudents = 0;

    allExperiencesOrInstitutions.forEach((experienceMap) => {
        experienceMap.forEach((cellData) => {
            if (cellData) {
                totalCurrent += cellData.currentTotalAvailability || 0;
                totalOriginal += cellData.originalTotalAvailability || 0;
                totalPreceptors += Object.keys(cellData.preceptors || {}).length;
                totalStudents += Object.values(cellData.preceptors || {}).reduce(
                    (sum, preceptor) => sum + (preceptor.students?.length || 0),
                    0
                );
            }
        });
    });

    const firstColumnWidth = sortedRotations.length > 0 ? '36%' : '100%';
    const rotationColumnWidth =
        sortedRotations.length > 0 ? `${(64 / sortedRotations.length).toFixed(4)}%` : '0%';

    const selectedCellData = selectedCell ? getCellData(selectedCell.name, selectedCell.rotation) : null;
    const selectedRotationHasStudent = selectedCell
        ? isStudentAssignedInRotation(selectedCell.rotation)
        : false;
    const modalContent = selectedCell && selectedCellData ? (
        <div
            style={modalStyles.backdrop}
            onClick={closeCellModal}
            onWheel={(event) => {
                if (event.target !== event.currentTarget) {
                    return;
                }

                event.preventDefault();
                const currentY = window.scrollY;
                const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
                const nextY = Math.min(Math.max(currentY + event.deltaY, 0), maxY);
                if (nextY !== currentY) {
                    window.scrollTo({ top: nextY, left: 0, behavior: 'auto' });
                }
            }}
        >
            <div style={modalStyles.card} onClick={(event) => event.stopPropagation()}>
                <div style={modalStyles.head}>
                    <div>
                        <h4 style={modalStyles.headTitle}>Cell Details</h4>
                        <p style={modalStyles.headSub}>
                            {selectedCell.name} | Rotation {selectedCell.rotation}
                        </p>
                    </div>
                    <button
                        type="button"
                        style={modalStyles.closeButton}
                        onClick={closeCellModal}
                        aria-label="Close popup"
                        title="Close"
                    >
                        &times;
                    </button>
                </div>

                <div style={modalStyles.summary}>
                    <div style={modalStyles.summaryCard}>
                        <div style={modalStyles.summaryLabel}>Availability</div>
                        <div style={modalStyles.summaryValue}>
                            {selectedCellData.currentTotalAvailability || 0}/
                            {selectedCellData.originalTotalAvailability || 0}
                        </div>
                    </div>
                    <div style={modalStyles.summaryCard}>
                        <div style={modalStyles.summaryLabel}>Preceptors</div>
                        <div style={modalStyles.summaryValue}>
                            {Object.keys(selectedCellData.preceptors || {}).length}
                        </div>
                    </div>
                    <div style={modalStyles.summaryCard}>
                        <div style={modalStyles.summaryLabel}>Students</div>
                        <div style={modalStyles.summaryValue}>
                            {Object.values(selectedCellData.preceptors || {}).reduce(
                                (sum, preceptor) => sum + (preceptor.students?.length || 0),
                                0
                            )}
                        </div>
                    </div>
                </div>

                <div style={modalStyles.body}>
                    {Object.entries(selectedCellData.preceptors || {}).map(
                        ([preceptorName, preceptorData]) => {
                            const students = getMergedStudentRows(preceptorData);
                            const preceptorId = getPreceptorId(preceptorName, preceptorData);
                            const availabilityEditKey = getAvailabilityEditKey(preceptorName, preceptorData);
                            const availabilityEdit = availabilityEditKey ? availabilityEdits[availabilityEditKey] : null;
                            const isEditingAvailability = Boolean(availabilityEdit);
                            const isSavingAvailability = Boolean(availabilityEdit?.isSaving);
                            const draftAvailability =
                                availabilityEdit?.draft?.[selectedCell.rotation] !== undefined
                                    ? availabilityEdit.draft[selectedCell.rotation]
                                    : parseInt(preceptorData.availability, 10) || 0;
                            const hasSelectedHere = selectedStudent && preceptorHasSelectedStudent(preceptorData);
                            const canAddSelected =
                                Boolean(selectedStudent) &&
                                !selectedRotationHasStudent &&
                                !preceptorHasSelectedStudent(preceptorData) &&
                                (parseInt(preceptorData.availability, 10) || 0) > 0;

                            return (
                                <section key={preceptorName} style={modalStyles.preceptorCard}>
                                    <header style={modalStyles.preceptorHead}>
                                        <h5 style={modalStyles.preceptorName}>{preceptorName}</h5>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={modalStyles.availabilityBadge}>
                                                {preceptorData.availability} slot
                                                {preceptorData.availability !== 1 ? 's' : ''}
                                            </span>
                                            {!isEditingAvailability ? (
                                                <button
                                                    type="button"
                                                    style={modalStyles.availabilityEditBtn}
                                                    disabled={!curriculumId}
                                                    onClick={() =>
                                                        handleAvailabilityDraftChange(
                                                            preceptorName,
                                                            preceptorData,
                                                            selectedCell.rotation,
                                                            parseInt(preceptorData.availability, 10) || 0
                                                        )
                                                    }
                                                    title={
                                                        !curriculumId
                                                            ? 'Curriculum ID unavailable for availability edits'
                                                            : 'Edit availability for this preceptor'
                                                    }
                                                >
                                                    Edit availability
                                                </button>
                                            ) : (
                                                <div style={modalStyles.availabilityEditInline}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={99}
                                                        value={draftAvailability}
                                                        onChange={(event) =>
                                                            handleAvailabilityDraftChange(
                                                                preceptorName,
                                                                preceptorData,
                                                                selectedCell.rotation,
                                                                event.target.value
                                                            )
                                                        }
                                                        style={modalStyles.availabilityEditor}
                                                        disabled={isSavingAvailability}
                                                    />
                                                    <button
                                                        type="button"
                                                        style={modalStyles.availabilitySaveBtn}
                                                        disabled={isSavingAvailability}
                                                        onClick={() =>
                                                            saveAvailabilityForPreceptor(
                                                                preceptorName,
                                                                preceptorData,
                                                                selectedCell.rotation
                                                            )
                                                        }
                                                    >
                                                        {isSavingAvailability ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={modalStyles.availabilityCancelBtn}
                                                        disabled={isSavingAvailability}
                                                        onClick={() => cancelAvailabilityEdit(preceptorName, preceptorData)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </header>

                                    <div style={modalStyles.studentsBox}>
                                        <p style={modalStyles.studentsLabel}>
                                            Assigned Students ({students.length})
                                        </p>

                                        {students.length > 0 ? (
                                            <div style={modalStyles.studentsList}>
                                                {students.map((student, index) => {
                                                    const isSelected = Boolean(selectedStudent) && matchesSelectedStudent(student.token);
                                                    return (
                                                        <span
                                                            key={`${student.token}-${index}`}
                                                            style={{
                                                                ...modalStyles.studentChip,
                                                                background: isSelected
                                                                    ? 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)'
                                                                    : modalStyles.studentChip.background,
                                                                border: isSelected
                                                                    ? '1px solid #16a34a'
                                                                    : modalStyles.studentChip.border,
                                                                color: isSelected ? '#166534' : modalStyles.studentChip.color,
                                                                fontWeight: isSelected ? 700 : modalStyles.studentChip.fontWeight
                                                            }}
                                                            title={isSelected ? 'Selected student' : undefined}
                                                        >
                                                            <span>{student.displayName}</span>
                                                            <span style={{ fontSize: '0.72rem', color: student.isCoprecepted ? '#7c2d12' : '#475569', fontWeight: 700, textTransform: 'uppercase' }}>
                                                                {student.isCoprecepted ? 'Co' : 'Pr'}
                                                            </span>
                                                            {isSelected && (
                                                                <button
                                                                    type="button"
                                                                    style={modalStyles.studentRemoveBtn}
                                                                    onClick={() => removeStudentFromRotation(selectedCell.rotation)}
                                                                    aria-label={`Remove ${student.displayName}`}
                                                                    title={`Remove ${student.displayName}`}
                                                                >
                                                                    x
                                                                </button>
                                                            )}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p style={modalStyles.emptyMessage}>No students assigned.</p>
                                        )}

                                        {canAddSelected && (
                                            <button
                                                type="button"
                                                style={modalStyles.addStudentBtn}
                                                onClick={() =>
                                                    addStudentToPreceptor(
                                                        selectedCell.rotation,
                                                        selectedCell.name,
                                                        preceptorName
                                                    )
                                                }
                                            >
                                                Add {selectedStudentLabel}
                                            </button>
                                        )}

                                        {hasSelectedHere && (
                                            <p style={{ ...modalStyles.emptyMessage, color: '#166534', fontStyle: 'normal' }}>
                                                Selected student is currently assigned here.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            );
                        }
                    )}
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <div style={tableStyles.wrapper}>
                {hasPendingEdits && (
                    <div style={tableStyles.actionsBar}>
                        <p style={tableStyles.actionsText}>
                            {`Unsaved changes for ${selectedStudentLabel}. Save or cancel before leaving.`}
                        </p>
                        <div style={tableStyles.actionsButtons}>
                            <button
                                type="button"
                                style={tableStyles.cancelButton}
                                onClick={cancelSelectedStudentScheduleChanges}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={tableStyles.saveButton}
                                onClick={saveAllChanges}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                )}

                {saveError && (
                    <div style={{ ...tableStyles.statusLine, color: '#b91c1c' }}>
                        {saveError}
                    </div>
                )}
                {saveSuccess && (
                    <div style={{ ...tableStyles.statusLine, color: '#166534' }}>
                        {saveSuccess}
                    </div>
                )}

                <div style={tableStyles.tableContainer}>
                    <table
                        style={tableStyles.table}
                        aria-label={`${axisLabel} availability matrix ${curriculumName ? `for ${curriculumName}` : ''}`.trim()}
                    >
                        <colgroup>
                            <col style={{ width: firstColumnWidth }} />
                            {sortedRotations.map((rotation) => (
                                <col key={`col-${rotation}`} style={{ width: rotationColumnWidth }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={tableStyles.axisCell}>{axisLabel}</th>
                                <th colSpan={sortedRotations.length} style={tableStyles.axisNumbersLabel}>
                                    Rotation
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedExperiencesOrInstitutions.map((experienceName, rowIdx) => (
                                <tr key={experienceName}>
                                    <th
                                        style={{
                                            ...tableStyles.rowHeader,
                                            background: rowIdx % 2 === 0 ? '#ffffff' : '#f9fbff'
                                        }}
                                        title={experienceName}
                                    >
                                        {experienceName}
                                    </th>
                                    {sortedRotations.map((rotation) => {
                                        const cellData = getCellData(experienceName, rotation);
                                        const current = cellData?.currentTotalAvailability || 0;
                                        const original = cellData?.originalTotalAvailability || 0;
                                        const hasData = Boolean(cellData) && Object.keys(cellData.preceptors || {}).length > 0;
                                        const hasSelectedStudent = cellContainsStudent(cellData);
                                        const isSelected =
                                            selectedCell &&
                                            selectedCell.name === experienceName &&
                                            selectedCell.rotation === rotation;

                                        let cellBackground = '#ffffff';
                                        let border = '1px solid transparent';
                                        if (hasData) {
                                            cellBackground = '#f8fbff';
                                        }
                                        if (hasSelectedStudent) {
                                            cellBackground = palette.successSoft;
                                            border = `1px solid ${palette.successBorder}`;
                                        }
                                        if (isSelected) {
                                            cellBackground = '#edf4ff';
                                            border = '1px solid #5c87b7';
                                        }

                                        return (
                                            <td key={`${experienceName}-${rotation}`} style={tableStyles.dataCell}>
                                                <button
                                                    type="button"
                                                    onClick={() => openCellModal(experienceName, rotation)}
                                                    disabled={!hasData}
                                                    style={{
                                                        width: '100%',
                                                        minHeight: '39px',
                                                        borderRadius: '6px',
                                                        border,
                                                        background: cellBackground,
                                                        color: hasData ? palette.text : '#96a3b4',
                                                        fontSize: '0.78rem',
                                                        fontWeight: 700,
                                                        cursor: hasData ? 'pointer' : 'default',
                                                        padding: '0.15rem 0.25rem',
                                                        outline: 'none',
                                                        boxShadow: hasData
                                                            ? 'inset 0 1px 0 rgba(255, 255, 255, 0.85)'
                                                            : 'none',
                                                        transition: 'all 0.18s ease'
                                                    }}
                                                    onMouseEnter={(event) => {
                                                        if (!hasData || isSelected) {
                                                            return;
                                                        }
                                                        event.currentTarget.style.transform = 'translateY(-1px)';
                                                        event.currentTarget.style.boxShadow =
                                                            '0 4px 10px rgba(27, 42, 58, 0.12)';
                                                    }}
                                                    onMouseLeave={(event) => {
                                                        if (!hasData || isSelected) {
                                                            return;
                                                        }
                                                        event.currentTarget.style.transform = 'translateY(0)';
                                                        event.currentTarget.style.boxShadow =
                                                            'inset 0 1px 0 rgba(255, 255, 255, 0.85)';
                                                    }}
                                                    title={
                                                        hasData
                                                            ? `${experienceName} / Rotation ${rotation}`
                                                            : 'No preceptor data in this cell'
                                                    }
                                                >
                                                    {cellData ? `${current}/${original}` : '-'}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={tableStyles.footer}>
                    <span style={tableStyles.footerMetric}>Total Availability: {totalCurrent}/{totalOriginal}</span>
                    <span style={tableStyles.footerMetric}>Total Preceptors: {totalPreceptors}</span>
                    <span style={tableStyles.footerMetric}>Total Students: {totalStudents}</span>
                    <span style={tableStyles.footerMetric}>
                        Utilization: {totalOriginal > 0 ? `${Math.round((totalStudents / totalOriginal) * 100)}%` : '0%'}
                    </span>
                </div>
            </div>

            {modalContent && typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null}
        </>
    );
};

export default AvailabilityMatrixTable;
